import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { XMarkIcon, BeakerIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { trlLevels } from './TRLInfo';

function TRLModal({ isOpen, onClose, selectedLevel, onSelectLevel }) {
  const selectedTRL = trlLevels.find(trl => trl.level === selectedLevel);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl max-h-[95vh] transform overflow-hidden rounded-3xl bg-white dark:bg-gray-900 shadow-2xl transition-all flex flex-col">
                {/* Header */}
                <div className="relative bg-gradient-to-r from-primary-700 to-primary-600 dark:from-primary-800 dark:to-primary-700 p-4 md:p-6 flex-shrink-0">
                  <button
                    onClick={onClose}
                    className="absolute top-3 left-3 md:top-4 md:left-4 p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-all backdrop-blur-sm z-10"
                  >
                    <XMarkIcon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  </button>
                  <Dialog.Title className="text-center pr-8">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-2">
                      <BeakerIcon className="h-8 w-8 md:h-10 md:w-10 text-secondary-400" />
                      <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                        سطوح آمادگی فناوری (TRL)
                      </h3>
                    </div>
                    <p className="text-white/90 text-sm sm:text-base md:text-lg px-4">
                      Technology Readiness Level - معیاری برای ارزیابی میزان آمادگی فناوری
                    </p>
                  </Dialog.Title>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                  <div className="space-y-3 md:space-y-4">
                    {trlLevels.map((trl, index) => (
                      <motion.div
                        key={trl.level}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        onClick={() => onSelectLevel(trl.level)}
                        className={`relative cursor-pointer rounded-xl md:rounded-2xl border-2 p-3 md:p-5 transition-all hover:shadow-xl active:scale-98 ${
                          selectedLevel === trl.level
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 shadow-lg'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-300 dark:hover:border-primary-600'
                        }`}
                      >
                        <div className="flex items-start gap-3 md:gap-4">
                          {/* Level Number */}
                          <div
                            className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl bg-gradient-to-br ${trl.color} flex items-center justify-center text-white font-bold text-lg md:text-2xl shadow-lg`}
                          >
                            {trl.level}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h4 className="text-base md:text-xl font-bold text-gray-800 dark:text-gray-100 leading-tight">
                                {trl.title}
                              </h4>
                              {selectedLevel === trl.level && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="flex-shrink-0"
                                >
                                  <CheckCircleIcon className="h-5 w-5 md:h-6 md:w-6 text-primary-600 dark:text-primary-400" />
                                </motion.div>
                              )}
                            </div>
                            <p className="text-xs md:text-base text-gray-600 dark:text-gray-300 mb-2 md:mb-3 leading-snug">
                              {trl.shortDesc}
                            </p>
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg md:rounded-xl p-2 md:p-3 border border-gray-200 dark:border-gray-600">
                              <p className="text-xs md:text-base text-gray-700 dark:text-gray-200 leading-relaxed text-justify">
                                {trl.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 dark:bg-gray-800 p-4 md:p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end">
                    <button
                      onClick={onClose}
                      className="px-5 md:px-6 py-2.5 md:py-3 rounded-xl font-bold text-sm md:text-base text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                    >
                      بستن
                    </button>
                    {selectedTRL && (
                      <button
                        onClick={onClose}
                        className="px-6 md:px-8 py-2.5 md:py-3 rounded-xl font-bold text-sm md:text-base text-white bg-gradient-to-r from-primary-700 to-primary-600 hover:from-primary-600 hover:to-primary-500 shadow-lg transition-all"
                      >
                        تایید انتخاب - سطح {selectedTRL.level}
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default TRLModal;
